import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AgentLayout from "../components/AgentLayout";
import NavButton from "../components/NavButton";
import axiosInstance from "../../../utils/axiosInstance";
import { getMediaUrl } from "../../../utils/mediaUrl";
import "../../client/components/jquery-global";
import $ from "jquery";
import "datatables.net-dt";
import "datatables.net-bs4/js/dataTables.bootstrap4.js";
import "datatables.net-bs4/css/dataTables.bootstrap4.css";
import "datatables.net-responsive-bs4/js/responsive.bootstrap4.js";
import "datatables.net-responsive-bs4/css/responsive.bootstrap4.css";

const DEPOSIT_TYPES = [
  { type: 1, label: "PKR", formType: "bank" },
  { type: 4, label: "JAZZCASH", formType: "wallet" },
  { type: 5, label: "EASYPAISA", formType: "wallet" },
  { type: 2, label: "USDT", formType: "usdt" },
];

const MULTI_ACCOUNT_TYPES = [1, 4, 5];

function escapeHtml(str = "") {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function compressImageFile(file, maxSize = 800, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(maxSize / width, maxSize / height, 1);
        width = Math.max(1, Math.round(width * scale));
        height = Math.max(1, Math.round(height * scale));

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = reader.result?.toString() || "";
    };
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.readAsDataURL(file);
  });
}

const SetPaymentDetails = () => {
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.userStore);
  const allowAutoDeposit = !!userInfo.data?.allow_auto_deposit;

  const [selectedType, setSelectedType] = useState(1);
  const [bankName, setBankName] = useState("");
  const [accountTitle, setAccountTitle] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bep20Address, setBep20Address] = useState("");
  const [depositId, setDepositId] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [qrcodeBase64, setQrcodeBase64] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({ text: "", isError: false });
  const [fileInputKey, setFileInputKey] = useState(0);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  const tableRef = useRef(null);
  const deleteAccountRef = useRef(null);

  const selectedTypeConfig =
    DEPOSIT_TYPES.find((item) => item.type === selectedType) || DEPOSIT_TYPES[0];
  const selectedLabel = selectedTypeConfig.label;
  const isWalletForm = selectedTypeConfig.formType === "wallet";
  const isUsdtForm = selectedTypeConfig.formType === "usdt";
  const isMultiAccount = MULTI_ACCOUNT_TYPES.includes(selectedType);

  const resetFormFields = () => {
    setBankName("");
    setAccountTitle("");
    setAccountNumber("");
    setBep20Address("");
    setDepositId("");
    setPreviewUrl("");
    setQrcodeBase64("");
  };

  const applyLoadedData = (data) => {
    if (!data) {
      resetFormFields();
      return;
    }

    setBankName(data.bank_name || "");
    setAccountTitle(data.account_title || "");
    setAccountNumber(data.account_number || "");
    setBep20Address(data.bep20_address || "");
    setDepositId(data.deposit_id ? String(data.deposit_id) : "");
    setPreviewUrl(getMediaUrl(data.qrcode_path || ""));
    setQrcodeBase64("");
  };

  const loadDepositAddress = useCallback(async (type) => {
    setLoading(true);
    setMessage({ text: "", isError: false });
    if (MULTI_ACCOUNT_TYPES.includes(type)) {
      setAccounts([]);
      setShowAddForm(false);
    }
    try {
      const response = await axiosInstance.get(`/company/deposit-address/${type}`);

      if (response.data?.status) {
        if (MULTI_ACCOUNT_TYPES.includes(type)) {
          const rows = Array.isArray(response.data.data) ? response.data.data : [];
          setAccounts(rows);
          setShowAddForm(false);
          resetFormFields();
        } else {
          setAccounts([]);
          applyLoadedData(response.data.data);
        }
      } else if (MULTI_ACCOUNT_TYPES.includes(type)) {
        setAccounts([]);
        setShowAddForm(false);
        resetFormFields();
      } else {
        setAccounts([]);
        resetFormFields();
      }
    } catch {
      if (MULTI_ACCOUNT_TYPES.includes(type)) {
        setAccounts([]);
      }
      resetFormFields();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!allowAutoDeposit) {
      navigate("/agent/accounts/chart");
      return;
    }
    loadDepositAddress(selectedType);
  }, [allowAutoDeposit, selectedType, loadDepositAddress, navigate]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const onChange = () => setIsNarrow(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const handleTypeChange = (type) => {
    setSelectedType(type);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await compressImageFile(file);
      setQrcodeBase64(result);
      setPreviewUrl(result);
    } catch {
      setMessage({
        text: "Failed to process the selected image",
        isError: true,
      });
    }
  };

  const validatePaymentDetails = () => {
    if (isWalletForm || selectedTypeConfig.formType === "bank") {
      if (!accountTitle.trim()) return "Account title is required";
      if (!accountNumber.trim()) return "Account number is required";
    }

    if (selectedTypeConfig.formType === "bank") {
      if (!bankName.trim()) return "Bank name is required";
    }

    if (isUsdtForm) {
      if (!bep20Address.trim()) return "Bep20 address is required";
    }

    const hasQrcode = !!qrcodeBase64 || !!previewUrl;
    if (!hasQrcode) {
      return "QR code is required";
    }

    return null;
  };

  const handleSubmit = async () => {
    setSaving(true);
    setMessage({ text: "", isError: false });

    const validationError = validatePaymentDetails();
    if (validationError) {
      setMessage({ text: validationError, isError: true });
      setSaving(false);
      return;
    }

    try {
      const payload = {
        type: selectedType,
      };

      if (isWalletForm || selectedTypeConfig.formType === "bank") {
        payload.account_title = accountTitle;
        payload.account_number = accountNumber;
      } else if (isUsdtForm) {
        payload.bep20_address = bep20Address;
      }

      if (selectedTypeConfig.formType === "bank") {
        payload.bank_name = bankName;
      }

      if (depositId) {
        payload.deposit_id = depositId;
      }
      if (qrcodeBase64) {
        payload.qrcode_base64 = qrcodeBase64;
      }

      const response = await axiosInstance.post(
        "/company/save-deposit-address",
        payload
      );

      if (response.data?.status) {
        setQrcodeBase64("");
        setFileInputKey((k) => k + 1);
        if (isMultiAccount) {
          setShowAddForm(false);
          resetFormFields();
          await loadDepositAddress(selectedType);
        } else {
          applyLoadedData(response.data.data);
        }
        setMessage({
          text: response.data.message || "Deposit address saved successfully",
          isError: false,
        });
      } else {
        setMessage({
          text: response.data?.message || "Failed to save payment details",
          isError: true,
        });
      }
    } catch (error) {
      const isPayloadTooLarge =
        error?.response?.status === 413 ||
        /entity too large|payload too large/i.test(
          String(error?.response?.data?.message || error?.message || "")
        );

      setMessage({
        text: isPayloadTooLarge
          ? "Image is too large. Please choose a smaller QR code image."
          : error?.response?.data?.message ||
            "Failed to save payment details",
        isError: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!id || deleting) return;
    if (!window.confirm("Delete this account?")) return;

    setDeleting(true);
    setMessage({ text: "", isError: false });
    try {
      const response = await axiosInstance.delete(`/company/deposit-address/${id}`);
      if (response.data?.status) {
        await loadDepositAddress(selectedType);
        setMessage({
          text: response.data.message || "Account deleted successfully",
          isError: false,
        });
      } else {
        setMessage({
          text: response.data?.message || "Failed to delete account",
          isError: true,
        });
      }
    } catch (error) {
      setMessage({
        text: error?.response?.data?.message || "Failed to delete account",
        isError: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  deleteAccountRef.current = handleDeleteAccount;

  const columns = useMemo(() => {
    const textCol = (title, field, extra = {}) => ({
      title,
      data: field,
      render: (value) => escapeHtml(value || ""),
      ...extra,
    });
    const detailLine = (label, value) =>
      `<div class="pay-detail-line"><span class="pay-detail-label">${escapeHtml(
        label
      )}</span><span class="pay-detail-value">${escapeHtml(value || "-")}</span></div>`;
    const fields = isNarrow
      ? [
          {
            title: "Account",
            data: null,
            render: (_value, type, row) => {
              const bank = row?.bank_name || "";
              const title = row?.account_title || "";
              const number = row?.account_number || "";
              if (type !== "display") {
                return [bank, title, number].filter(Boolean).join(" ");
              }
              const lines = [];
              if (selectedTypeConfig.formType === "bank") {
                lines.push(detailLine("Bank", bank));
              }
              lines.push(detailLine("Title", title));
              lines.push(detailLine("Number", number));
              return `<div class="pay-detail-stack">${lines.join("")}</div>`;
            },
          },
        ]
      : selectedTypeConfig.formType === "bank"
        ? [
            textCol("Bank Name", "bank_name"),
            textCol("Account Title", "account_title"),
            textCol("Account Number", "account_number", {
              type: "string",
              className: "dt-left pay-num",
            }),
          ]
        : [
            textCol("Account Title", "account_title"),
            textCol("Account Number", "account_number", {
              type: "string",
              className: "dt-left pay-num",
            }),
          ];

    return [
      ...fields,
      {
        title: "QR Code",
        data: "qrcode_path",
        orderable: false,
        className: "pay-qr-col",
        render: (value) => {
          const url = getMediaUrl(value || "");
          if (!url) return "";
          return `<img src="${escapeHtml(url)}" alt="QR" style="height:48px;width:48px;object-fit:contain;border:1px solid #d1cfcf;border-radius:4px;" />`;
        },
      },
      {
        title: "Action",
        data: "deposit_id",
        orderable: false,
        className: "no-export pay-action-col",
        render: (value) =>
          `<button type="button" class="btn btn-sm btn-danger site_family dt-delete-account" data-id="${escapeHtml(
            String(value || "")
          )}">Delete</button>`,
      },
    ];
  }, [isNarrow, selectedTypeConfig.formType]);

  useEffect(() => {
    if (!isMultiAccount) return undefined;
    const el = tableRef.current;
    if (!el) return undefined;

    const $table = $(el);
    if ($.fn.dataTable.isDataTable($table)) {
      $table.off("click", "button.dt-delete-account");
      try {
        $table.DataTable().destroy();
      } catch (error) {
        console.warn("Error destroying payment DataTable:", error);
      }
    }

    let dt;
    try {
      dt = $table.DataTable({
        data: accounts,
        columns,
        destroy: true,
        autoWidth: false,
        ordering: true,
        pageLength: 10,
        lengthMenu: [10, 25, 50, 100],
        lengthChange: true,
        searching: true,
        paging: true,
        info: true,
        orderClasses: false,
        responsive: false,
        language: {
          emptyTable: "No data available in table",
          zeroRecords: "No matching records found",
          lengthMenu: "Show _MENU_ entries",
          search: "Search:",
          info: "Showing _START_ to _END_ of _TOTAL_ entries",
          infoEmpty: "Showing 0 to 0 of 0 entries",
          infoFiltered: "(filtered from _MAX_ total entries)",
          paginate: { previous: "Previous", next: "Next" },
        },
      });
      $table.on("click", "button.dt-delete-account", function onDeleteClick() {
        const id = $(this).attr("data-id");
        deleteAccountRef.current?.(id);
      });
    } catch (error) {
      console.error("Error initializing payment DataTable:", error);
    }

    return () => {
      if (!dt) return;
      $table.off("click", "button.dt-delete-account");
      try {
        dt.destroy();
      } catch (error) {
        console.warn("Error destroying payment DataTable:", error);
      }
    };
  }, [accounts, columns, isMultiAccount]);

  useEffect(() => {
    if (!showAddForm) return undefined;
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [showAddForm]);

  return (
    <AgentLayout>
      <div className="container-fluid">
        <div className="bets-section-wrapper">
          <NavButton />

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <style>
                    {`
                      .btn-success {
                        color: #fff;
                        background-color: #00b181;
                        border-color: #28a745;
                      }

                      .btn_top {
                        display: inline-block;
                        font-weight: 400;
                        text-align: center;
                        white-space: nowrap;
                        vertical-align: middle;
                        user-select: none;
                        border: 1px solid transparent;
                        padding: .375rem .75rem;
                        font-size: .875rem;
                        line-height: 1.5;
                        border-radius: .25rem;
                        transition: color .15s ease-in-out, background-color .15s ease-in-out, border-color .15s ease-in-out, box-shadow .15s ease-in-out;
                        margin-right: 5px;
                        margin-bottom: 5px;
                        color: #fff;
                        cursor: pointer;
                      }

                      .payment-accounts-dt .dataTables_wrapper {
                        max-width: 100%;
                        overflow-x: hidden;
                      }

                      .payment-accounts-dt table.dataTable {
                        width: 100% !important;
                      }

                      .payment-accounts-dt table.dataTable th,
                      .payment-accounts-dt table.dataTable td {
                        vertical-align: middle;
                      }

                      .payment-accounts-dt .pay-qr-col,
                      .payment-accounts-dt .pay-action-col {
                        width: 1%;
                        white-space: nowrap;
                        text-align: center;
                      }

                      .pay-detail-stack {
                        display: flex;
                        flex-direction: column;
                        gap: 2px;
                        min-width: 0;
                      }

                      .pay-detail-line {
                        display: flex;
                        gap: 6px;
                        align-items: flex-start;
                        line-height: 1.35;
                      }

                      .pay-detail-label {
                        flex: 0 0 58px;
                        font-weight: 700;
                        text-align: left;
                      }

                      .pay-detail-value {
                        min-width: 0;
                        overflow-wrap: anywhere;
                        word-break: break-word;
                        text-align: left;
                      }

                      .payment-accounts-dt table.dataTable th.pay-num,
                      .payment-accounts-dt table.dataTable td.pay-num,
                      .payment-accounts-dt table.dataTable th.pay-num div.dt-column-header,
                      .payment-accounts-dt table.dataTable td.pay-num div.dt-column-header {
                        text-align: left !important;
                        flex-direction: row !important;
                        justify-content: flex-start !important;
                      }

                      @media (max-width: 767px) {
                        .payment-accounts-dt table.dataTable {
                          table-layout: fixed !important;
                        }

                        .payment-accounts-dt .pay-qr-col {
                          width: 64px !important;
                        }

                        .payment-accounts-dt .pay-action-col {
                          width: 84px !important;
                        }
                      }
                    `}
                  </style>

                  {DEPOSIT_TYPES.map((item) => (
                    <button
                      key={item.type}
                      type="button"
                      style={{ backgroundColor: "#02b181" }}
                      className={`btn_top ${
                        selectedType === item.type ? "btn-primary" : "btn-primary"
                      }`}
                      onClick={() => handleTypeChange(item.type)}
                    >
                      Deposit {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {isMultiAccount && (
              <div className="col-12">
                <div className="card">
                  <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                    <strong className="site_family">Deposit {selectedLabel} Address</strong>
                    <button
                      type="button"
                      className="btn_top btn-primary"
                      style={{ backgroundColor: "#02b181", marginBottom: 0 }}
                      onClick={() => {
                        resetFormFields();
                        setFileInputKey((k) => k + 1);
                        setShowAddForm(true);
                        setMessage({ text: "", isError: false });
                      }}
                    >
                      Add account
                    </button>
                  </div>
                  <div className="card-body payment-accounts-dt">
                    {loading && (
                      <div className="text-center mb-2">
                        <div className="spinner-border spinner-border-sm text-success" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      </div>
                    )}
                    {message.text && !showAddForm && (
                      <div
                        className={`mb-2 site_family ${
                          message.isError ? "text-danger" : "text-success"
                        }`}
                        style={{ fontSize: "13px" }}
                      >
                        {message.text}
                      </div>
                    )}
                    <div key={`payment-table-${selectedType}-${isNarrow ? "narrow" : "wide"}`}>
                      <table
                        ref={tableRef}
                        className="table table-bordered table-striped table-sm site_family display"
                      >
                        <thead>
                          <tr>
                            {columns.map((col) => (
                              <th key={col.title}>{col.title}</th>
                            ))}
                          </tr>
                        </thead>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isUsdtForm && (
            <div className="col-12 col-md-6" id="editsection2">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                <div className="card">
                  <div className="card-header">
                    <strong>
                      Deposit {selectedLabel} Address
                    </strong>
                  </div>
                  <div className="card-body">
                    {loading ? (
                      <div className="site_family">Loading...</div>
                    ) : (
                      <>
                        {isWalletForm ? (
                          <>
                            <div className="form-group row">
                              <label className="col-4 col-md-3 col-form-label site_family">
                                Account Title
                              </label>
                              <div className="col-8 col-md-6">
                                <input
                                  className="form-control"
                                  type="text"
                                  id="account_title"
                                  name="account_title"
                                  value={accountTitle}
                                  onChange={(e) => setAccountTitle(e.target.value)}
                                />
                              </div>
                            </div>

                            <div className="form-group row">
                              <label className="col-4 col-md-3 col-form-label site_family">
                                Account Number
                              </label>
                              <div className="col-8 col-md-6">
                                <input
                                  className="form-control"
                                  type="text"
                                  id="account_number"
                                  name="account_number"
                                  value={accountNumber}
                                  onChange={(e) => setAccountNumber(e.target.value)}
                                />
                              </div>
                            </div>
                          </>
                        ) : isUsdtForm ? (
                          <div className="form-group row">
                            <label className="col-4 col-md-3 col-form-label site_family">
                              Bep20 Address
                            </label>
                            <div className="col-8 col-md-6">
                              <input
                                className="form-control"
                                type="text"
                                id="bep20_address"
                                name="bep20_address"
                                value={bep20Address}
                                onChange={(e) => setBep20Address(e.target.value)}
                              />
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="form-group row">
                              <label className="col-4 col-md-3 col-form-label site_family">
                                Account Title
                              </label>
                              <div className="col-8 col-md-6">
                                <input
                                  className="form-control"
                                  type="text"
                                  id="account_title"
                                  name="account_title"
                                  value={accountTitle}
                                  onChange={(e) => setAccountTitle(e.target.value)}
                                />
                              </div>
                            </div>

                            <div className="form-group row">
                              <label className="col-4 col-md-3 col-form-label site_family">
                                Bank Name
                              </label>
                              <div className="col-8 col-md-6">
                                <input
                                  className="form-control"
                                  type="text"
                                  id="bank_name"
                                  name="bank_name"
                                  value={bankName}
                                  onChange={(e) => setBankName(e.target.value)}
                                />
                              </div>
                            </div>

                            <div className="form-group row">
                              <label className="col-4 col-md-3 col-form-label site_family">
                                Account Number
                              </label>
                              <div className="col-8 col-md-6">
                                <input
                                  className="form-control"
                                  type="text"
                                  id="account_number"
                                  name="account_number"
                                  value={accountNumber}
                                  onChange={(e) => setAccountNumber(e.target.value)}
                                />
                              </div>
                            </div>
                          </>
                        )}

                        <div className="form-group row">
                          <label className="col-4 col-md-3 col-form-label site_family">
                            Qrcode
                          </label>
                          <div className="col-8 col-md-6">
                            <input
                              key={`qrcode-${selectedType}-${fileInputKey}`}
                              type="file"
                              className="van-field__control"
                              placeholder="Qrcode"
                              name="file"
                              id="image_file"
                              accept="image/*"
                              onChange={handleFileChange}
                            />

                            {previewUrl && (
                              <div style={{ paddingBottom: "0px" }}>
                                <img
                                  src={previewUrl}
                                  alt="QR Code"
                                  style={{
                                    height: "180px",
                                    width: "180px",
                                    border: "2px solid #d1cfcf",
                                    borderRadius: "5px",
                                    marginTop: "15px",
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="card-footer editsbmtbtn">
                    <button
                      className="btn btn-primary editsbmtbtnin site_family"
                      type="submit"
                      disabled={saving || loading}
                    >
                      <strong>{saving ? "Saving..." : "Submit"}</strong>
                    </button>
                    {message.text && (
                      <div
                        className={`mt-2 site_family ${
                          message.isError ? "text-danger" : "text-success"
                        }`}
                        style={{ fontSize: "13px" }}
                      >
                        {message.text}
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>
            )}

            {isMultiAccount && showAddForm && (
              <>
                <div
                  className="modal-backdrop fade show"
                  style={{
                    zIndex: 1040,
                    backgroundColor: "transparent",
                    opacity: 1,
                    backdropFilter: "none",
                    WebkitBackdropFilter: "none",
                  }}
                  onClick={() => {
                    if (saving) return;
                    setShowAddForm(false);
                    resetFormFields();
                  }}
                />
                <div
                  className="modal fade show"
                  role="dialog"
                  style={{ display: "block", zIndex: 1050 }}
                  tabIndex={-1}
                >
                  <div className="modal-dialog modal-lg" role="document">
                    <div
                      className="modal-content"
                      style={{
                        backgroundColor: "#fff",
                        color: "#212529",
                        width: "100%",
                        maxWidth: "none",
                        maxHeight: "none",
                      }}
                    >
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleSubmit();
                        }}
                      >
                        <div
                          className="modal-header"
                          style={{
                            backgroundColor: "#fff",
                            color: "#212529",
                            borderBottom: "1px solid #dee2e6",
                          }}
                        >
                          <h5 className="modal-title site_family" style={{ color: "#212529" }}>
                            Add {selectedLabel} account
                          </h5>
                          <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            style={{ color: "#212529" }}
                            onClick={() => {
                              if (saving) return;
                              setShowAddForm(false);
                              resetFormFields();
                            }}
                          >
                            <span aria-hidden="true">&times;</span>
                          </button>
                        </div>
                        <div
                          className="modal-body"
                          style={{ backgroundColor: "#fff", color: "#212529" }}
                        >
                          {isWalletForm ? (
                            <>
                              <div className="form-group row">
                                <label className="col-4 col-form-label site_family">
                                  Account Title
                                </label>
                                <div className="col-8">
                                  <input
                                    className="form-control"
                                    type="text"
                                    value={accountTitle}
                                    onChange={(e) => setAccountTitle(e.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="form-group row">
                                <label className="col-4 col-form-label site_family">
                                  Account Number
                                </label>
                                <div className="col-8">
                                  <input
                                    className="form-control"
                                    type="text"
                                    value={accountNumber}
                                    onChange={(e) => setAccountNumber(e.target.value)}
                                  />
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="form-group row">
                                <label className="col-4 col-form-label site_family">
                                  Account Title
                                </label>
                                <div className="col-8">
                                  <input
                                    className="form-control"
                                    type="text"
                                    value={accountTitle}
                                    onChange={(e) => setAccountTitle(e.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="form-group row">
                                <label className="col-4 col-form-label site_family">
                                  Bank Name
                                </label>
                                <div className="col-8">
                                  <input
                                    className="form-control"
                                    type="text"
                                    value={bankName}
                                    onChange={(e) => setBankName(e.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="form-group row">
                                <label className="col-4 col-form-label site_family">
                                  Account Number
                                </label>
                                <div className="col-8">
                                  <input
                                    className="form-control"
                                    type="text"
                                    value={accountNumber}
                                    onChange={(e) => setAccountNumber(e.target.value)}
                                  />
                                </div>
                              </div>
                            </>
                          )}
                          <div className="form-group row">
                            <label className="col-4 col-form-label site_family">
                              Qrcode
                            </label>
                            <div className="col-8">
                              <input
                                key={`qrcode-modal-${selectedType}-${fileInputKey}`}
                                type="file"
                                className="van-field__control"
                                accept="image/*"
                                onChange={handleFileChange}
                              />
                              {previewUrl && (
                                <img
                                  src={previewUrl}
                                  alt="QR Code"
                                  style={{
                                    height: "180px",
                                    width: "180px",
                                    border: "2px solid #d1cfcf",
                                    borderRadius: "5px",
                                    marginTop: "15px",
                                  }}
                                />
                              )}
                            </div>
                          </div>
                          {message.text && (
                            <div
                              className={`site_family ${
                                message.isError ? "text-danger" : "text-success"
                              }`}
                              style={{ fontSize: "13px" }}
                            >
                              {message.text}
                            </div>
                          )}
                        </div>
                        <div
                          className="modal-footer"
                          style={{
                            backgroundColor: "#fff",
                            borderTop: "1px solid #dee2e6",
                          }}
                        >
                          <button
                            className="btn btn-secondary site_family"
                            type="button"
                            disabled={saving}
                            onClick={() => {
                              setShowAddForm(false);
                              resetFormFields();
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-primary site_family"
                            type="submit"
                            disabled={saving}
                            style={{ backgroundColor: "#02b181", borderColor: "#02b181" }}
                          >
                            <strong>{saving ? "Saving..." : "Submit"}</strong>
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </AgentLayout>
  );
};

export default SetPaymentDetails;
